import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function BottleCap() {
    return <svg viewBox="0 0 24 18" preserveAspectRatio="none" fill="none" aria-hidden="true">
        <path d="M5 11h14l-1 6H6l-1-6Z" fill="#a67c48" stroke="#805b30" strokeWidth=".8"/>
        <rect x="1" y="1" width="22" height="12" rx="2.5" fill="#c59c66" stroke="#805b30"/>
        <path d="M3 4h18M4 10h16" stroke="#dfbf8d" strokeWidth="1" strokeLinecap="round"/>
        <path d="m6 6 2 1m5-2 2 1m3 2 1-1M9 10l1-1M4 8h1" stroke="#96703f" strokeWidth=".9" strokeLinecap="round"/>
    </svg>;
}

export default function BackToTop() {
    const [visible, setVisible] = useState(false);
    const [fillLevel, setFillLevel] = useState(0);
    const [flight, setFlight] = useState(null);
    const capRef = useRef(null);
    const timerRef = useRef(null);
    const gradientId = useId();

    useEffect(() => {
        const updateProgress = () => {
            const scrollTop = Math.max(0, window.scrollY);
            const pageHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
            const maxScroll = Math.max(0, pageHeight - window.innerHeight);
            const progress = maxScroll > 100 ? (scrollTop - 100) / (maxScroll - 100) : 0;
            setVisible(scrollTop > 100);
            setFillLevel(Math.round(Math.min(1, Math.max(0, progress)) * 100) / 100);
        };
        updateProgress();
        window.addEventListener('scroll', updateProgress, { passive: true });
        window.addEventListener('resize', updateProgress);
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateProgress) : null;
        observer?.observe(document.body);
        return () => {
            window.removeEventListener('scroll', updateProgress);
            window.removeEventListener('resize', updateProgress);
            observer?.disconnect();
            window.clearTimeout(timerRef.current);
        };
    }, []);

    const scrollToTop = () => {
        if (flight) return;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion) {
            const rect = capRef.current.getBoundingClientRect();
            setFlight({ left: rect.left, top: rect.top, width:rect.width, height:rect.height });
            timerRef.current = window.setTimeout(() => setFlight(null), 1700);
        }
        window.scrollTo({ top: 0, behavior: reducedMotion ? 'instant' : 'smooth' });
    };

    if (!visible && !flight) return null;

    return (
        <>
            <button type="button" className={`ps-back-to-top${flight ? ' ps-back-to-top--launching' : ''}`}
                aria-label="Back to top" aria-disabled={Boolean(flight)} onClick={scrollToTop}>
                <span className="ps-back-to-top__tooltip" aria-hidden="true">Back to top</span>
                <svg className="ps-back-to-top__arrow" viewBox="0 0 16 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M8 17V3M3 8l5-5 5 5"/></svg>
                {!flight && <span ref={capRef} className="ps-back-to-top__cap"><BottleCap /></span>}
                <svg className="ps-back-to-top__bottle" viewBox="0 0 56 76" preserveAspectRatio="none" fill="none" aria-hidden="true" focusable="false">
                    <defs>
                        <linearGradient id={`${gradientId}-glass`} x1="11" y1="30" x2="45" y2="30" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#c3a06c" /><stop offset=".22" stopColor="#fff8e9" /><stop offset=".6" stopColor="#f5e5c2" /><stop offset="1" stopColor="#b58c51" />
                        </linearGradient>
                        <linearGradient id={`${gradientId}-oil`} x1="14" y1="8" x2="42" y2="69" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#d9a133" /><stop offset="1" stopColor="#a56c16" />
                        </linearGradient>
                        <clipPath id={`${gradientId}-interior`}>
                            <path d="M22 8h12v7c0 5 9 8 9 14v35c0 3-2 5-5 5H18c-3 0-5-2-5-5V29c0-6 9-10 9-14V8Z"/>
                        </clipPath>
                    </defs>
                    <path d="M20 5h16v9c0 3 9 8 9 14v37c0 5-3 7-8 7H19c-5 0-8-2-8-7V28c0-6 9-11 9-14V5Z" fill={`url(#${gradientId}-glass)`} stroke="#91682e" strokeWidth="1.2" strokeLinejoin="round" />
                    <g clipPath={`url(#${gradientId}-interior)`}>
                        <g className="ps-back-to-top__oil-level" data-fill-level={fillLevel} style={{transform:`translateY(${((1 - fillLevel) * 61).toFixed(2)}px)`}}>
                            <path d="M12 9c10-2 20 2 32 0v64H12V9Z" fill={`url(#${gradientId}-oil)`}/>
                            <path d="M12 9c10-2 20 2 32 0" stroke="#ffe6a0" strokeWidth="1.2"/>
                        </g>
                    </g>
                    {flight && <g className="ps-oil-overflow">
                        <g className="ps-oil-overflow__flow ps-oil-overflow__flow--left">
                            <path d="M21 8v8c0 5-8 8-8 16v17" stroke={`url(#${gradientId}-oil)`} strokeWidth="4.5" strokeLinecap="round"/>
                            <path d="M21 8v8c0 5-8 8-8 16v17" stroke="#ffebae" strokeWidth="1" strokeLinecap="round"/>
                        </g>
                        <g className="ps-oil-overflow__flow ps-oil-overflow__flow--right">
                            <path d="M35 8v8c0 5 8 8 8 16v9" stroke={`url(#${gradientId}-oil)`} strokeWidth="3.5" strokeLinecap="round"/>
                            <path d="M35 8v8c0 5 8 8 8 16v9" stroke="#ffe5a0" strokeWidth=".8" strokeLinecap="round"/>
                        </g>
                        <path className="ps-oil-overflow__rim" d="M19 8c0-4 4-6 9-6s9 2 9 6c0 3-4 5-9 5s-9-2-9-5Z" fill={`url(#${gradientId}-oil)`} stroke="#b48036" strokeWidth=".7"/>
                        <path className="ps-oil-overflow__rim" d="M22 6c3-2 9-2 12 0" stroke="#ffebae" strokeWidth="1.2" strokeLinecap="round"/>
                    </g>}
                    <path d="M17 28v34" stroke="#fff7de" strokeWidth="2" strokeLinecap="round" opacity=".65" />
                    <path d="M39 29v33" stroke="#8d621f" strokeWidth="1" strokeLinecap="round" opacity=".4" />
                    <rect x="15" y="39" width="26" height="24" rx="3" fill="#f5eedc" stroke="#a58c58" strokeWidth=".8" />
                    <image className="ps-back-to-top__brand" href="/assets/img/favicon.png" x="18" y="42" width="20" height="18" preserveAspectRatio="xMidYMid meet"/>
                    <path d="M20 15h16m-15 2h14" stroke="#b59a66" strokeWidth="1.1" strokeLinecap="round"/>
                    <rect x="18" y="4" width="20" height="4" rx="1.5" fill="#c5954e" stroke="#805b30" strokeWidth=".8"/>
                </svg>
                <span className="ps-back-to-top__caption" aria-hidden="true">TOP</span>
            </button>
            {flight && createPortal(
                <>
                <span className="ps-oil-cap-flight" aria-hidden="true"
                    onAnimationEnd={(event) => {
                        if (event.target === event.currentTarget) {
                            window.clearTimeout(timerRef.current);
                            setFlight(null);
                        }
                    }}
                    style={{ left: flight.left, top: flight.top, width:flight.width, height:flight.height, '--cap-travel': `${-(flight.top + 40)}px` }}>
                    <BottleCap />
                </span>
                </>, document.body
            )}
        </>
    );
}
